import React, { useState } from "react";
import { useParams } from "react-router-dom";
import {
  Text,
  Flex,
  Grid,
  Select,
  Badge,
  Box,
} from "@chakra-ui/react";
import { useAppContext } from "../../../contexts/AppContext";
import SingleFundContacts from "./Contacts";
import { Line, XAxis, Area, Tooltip, Legend, CartesianGrid, ResponsiveContainer, YAxis, ComposedChart } from "recharts";

const SingleFundOverview = () => {
  const { fundName } = useParams();
  const { data, formatMillions } = useAppContext();

  const formattedFundName = fundName.replace(/-/g, " ");
  const fund = data.find((fund) => fund.fundName === formattedFundName);
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);

  if (!fund) return <Text>Selectionnez un fonds pour voir les détails.</Text>;

  const generateDummyData = (year) => {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const totalCommitment = fund.factSheet.overview.totalCommitment;
    let commitment = totalCommitment * 0.2;
    let assetValue = commitment * 1.2;
    const commitmentResetFactor = 0.2 + Math.random() * 0.1;
  
    return months.map((month, index) => {
      if (index === 0 && year !== new Date().getFullYear()) {
        commitment = totalCommitment * commitmentResetFactor;
      } else if (index === 2 || index === 5 || index === 8 || Math.random() > 0.7) {
        commitment += totalCommitment * (Math.random() * 0.05 + 0.05);
        if (commitment > totalCommitment) commitment = totalCommitment;
      }
  
      assetValue = commitment * (1.1 + Math.random() * 0.5);
      assetValue += (Math.random() - 0.5) * 0.1 * totalCommitment;
  
      return {
        month,
        commitment: Math.round(commitment),
        totalCommitment: Math.round(totalCommitment), // Ensure totalCommitment is included for each month
        assetValue: Math.max(Math.round(assetValue), commitment),
      };
    });
  };

  const dummyData = generateDummyData(selectedYear);

  return (
    <Grid gap={8} direction="column" templateColumns={"repeat(12, 1fr)"}>
      <Flex gridColumn={{ lg: "span 8", base: "span 12" }} direction="column">        
        <Flex direction={"row"} gap={16} pb={8}>
          <Flex direction={"column"}>
            <Text>Commitment</Text>
            <Badge fontSize="xl" colorScheme="teal" w={"fit-content"}>
              {fund.fundCurrency.ISO} {formatMillions(fund.factSheet.overview.commitment)}
            </Badge>
          </Flex>

          <Flex direction={"column"}>
            <Text>Total commitment</Text>
            <Badge fontSize="xl" colorScheme="green" w={"fit-content"}>
              {fund.fundCurrency.ISO} {formatMillions(fund.factSheet.overview.totalCommitment)}
            </Badge>
          </Flex>

          <Flex direction={"column"}>
            <Text>Asset value</Text>
            <Badge fontSize="xl" colorScheme="orange" w={"fit-content"}>
              {fund.fundCurrency.ISO} {formatMillions(fund.factSheet.overview.assetValue)}
            </Badge>
          </Flex>
        </Flex>

        <Box>
          <Flex position={"absolute"} top={4} right={4} zIndex={1} bg={"white"}>
            <Select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} w="150px">
              {[...Array(5)].map((_, index) => {
                const year = currentYear - index;
                return <option key={year} value={year}>{year}</option>;
              })}
            </Select>
          </Flex>

          <ResponsiveContainer width="100%" height={400}>
            <ComposedChart data={dummyData}>
            <defs>
    <linearGradient id="colorTotalCommitment" x1="0" y1="0" x2="0" y2="1">
      <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.5}/>
      <stop offset="95%" stopColor="#82ca9d" stopOpacity={0}/>
    </linearGradient>
 
  </defs>
              <CartesianGrid strokeDasharray="1 1" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Area 
                type="monotone" 
                dataKey="totalCommitment" 
                stroke="#82ca9d" 
                strokeWidth={2}
                fill="url(#colorTotalCommitment)"
                fillOpacity={1}
                name="Total Commitment" 
              />
              <Line type="monotone" dataKey="commitment" stroke="teal" strokeWidth={2} name="Commitment" />
              <Line type="monotone" dataKey="assetValue" stroke="#ffc658" strokeWidth={2} name="Asset Value" />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      </Flex>

      <Flex gridColumn={{ lg: "span 4", base: "span 12" }} direction={"column"} gap={16} borderLeft={{ lg: "1px solid", base: "none" }} borderColor={{ lg: "grey.200", base: "none" }} pl={{ lg: 8, base: 0 }}>
        <SingleFundContacts />
      </Flex>
    </Grid>
  );
};

export default SingleFundOverview;
